import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Button, Chip, IconButton,
  Select, MenuItem, FormControl,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FilterListIcon from "@mui/icons-material/FilterList";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CloseIcon from "@mui/icons-material/Close";
import { fetchData } from "../../BaseUrl";

const WEEK_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const YEAR_RANGE = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i); // current-2 to current+2

function generateFallbackCalendar(year, month) {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const data = {};
  for (let d = 1; d <= daysInMonth; d++) {
    const day = new Date(year, month, d).getDay();
    if (day !== 0 && day !== 6) {
      data[d] = Math.random() > 0.2 ? "present" : "absent";
    }
  }
  return data;
}

export default function AttendanceCalendar({ open, onClose, student }) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [calData, setCalData] = useState({});

  const loadCalendar = useCallback(async () => {
    if (!student) return;
    try {
      const appId = student.applicationId || sessionStorage.getItem("applicationId");
      const response = await fetchData(`teacher/attendance/student/${appId}?year=${year}&month=${month + 1}`);
      if (response?.data) {
        setCalData(response.data);
      } else {
        setCalData(generateFallbackCalendar(year, month));
      }
    } catch (_) {
      setCalData(generateFallbackCalendar(year, month));
    }
  }, [student, year, month]);

  useEffect(() => {
    if (open) loadCalendar();
  }, [open, loadCalendar]);

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const presentCount = Object.values(calData).filter((v) => v === "present").length;
  const absentCount = Object.values(calData).filter((v) => v === "absent").length;
  const total = presentCount + absentCount;
  const overallPct = total > 0 ? Math.round((presentCount / total) * 100) : 0;

  const getDayColor = (day) => {
    if (!day) return "transparent";
    const isToday = day === today.getDate() && month === today.getMonth() && year === today.getFullYear();
    const status = calData[day];
    if (status === "present") return "#4caf50";
    if (status === "absent" || isToday) return "#f44336";
    return "#e0e0e0";
  };

  if (!open) return null;

  return (
    <Box sx={{ padding: "24px" }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton onClick={onClose} size="small"><ArrowBackIcon /></IconButton>
          <Typography sx={{ fontWeight: "bold", fontSize: "16px" }}>Attendance Calendar</Typography>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </Box>

      {/* Student info card */}
      {student && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, p: 2, backgroundColor: "#f8f9fa", borderRadius: "12px" }}>
          <Box sx={{ width: "50px", height: "50px", borderRadius: "50%", background: "linear-gradient(135deg,#4A7BA8,#244C77)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: "bold", fontSize: "18px" }}>
            {(student.studentName || "S").charAt(0)}
          </Box>
          <Box>
            <Typography sx={{ fontWeight: "bold", fontSize: "15px" }}>{student.studentName}</Typography>
            <Typography sx={{ fontSize: "12px", color: "#8F95B2" }}>Reg: {student.regNo}</Typography>
            <Typography sx={{ fontSize: "12px", color: "#8F95B2" }}>Nilai: {student.grade}</Typography>
          </Box>
          <Box sx={{ ml: "auto", display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Chip label={`${presentCount} Present`} size="small" sx={{ backgroundColor: "#e8f5e9", color: "#4caf50", fontWeight: "bold", fontSize: "12px", borderRadius: "20px" }} />
            <Chip label={`${absentCount} Absent`} size="small" sx={{ backgroundColor: "#ffebee", color: "#f44336", fontWeight: "bold", fontSize: "12px", borderRadius: "20px" }} />
            <Chip label={`${overallPct}% Overall`} size="small" sx={{ backgroundColor: "#e3f2fd", color: "#1976d2", fontWeight: "bold", fontSize: "12px", borderRadius: "20px" }} />
          </Box>
        </Box>
      )}

      {/* Controls: Filter + Export on left, Month/Year dropdowns on right */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1.5 }}>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button startIcon={<FilterListIcon />} sx={{ border: "1px solid #D5D7DA", borderRadius: "8px", color: "#8F95B2", textTransform: "none", fontSize: "13px", padding: "6px 14px" }}>Filters</Button>
          <Button startIcon={<FileDownloadIcon />} sx={{ border: "1px solid #D5D7DA", borderRadius: "8px", color: "#8F95B2", textTransform: "none", fontSize: "13px", padding: "6px 14px" }}>Export</Button>
        </Box>

        {/* ── Gap #15: Month & Year dropdown selectors ── */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <Select
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              sx={{ fontSize: "13px", borderRadius: "8px" }}
            >
              {MONTHS.map((m, i) => (
                <MenuItem key={i} value={i} sx={{ fontSize: "13px" }}>{m}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 90 }}>
            <Select
              value={year}
              onChange={(e) => setYear(e.target.value)}
              sx={{ fontSize: "13px", borderRadius: "8px" }}
            >
              {YEAR_RANGE.map((y) => (
                <MenuItem key={y} value={y} sx={{ fontSize: "13px" }}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Calendar grid */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: "6px" }}>
        {WEEK_DAYS.map((d) => (
          <Box key={d} sx={{ textAlign: "center", fontSize: "12px", color: "#8F95B2", fontWeight: "bold", py: "6px" }}>
            {d}
          </Box>
        ))}
        {cells.map((day, idx) => (
          <Box
            key={idx}
            sx={{
              textAlign: "center",
              py: "10px",
              borderRadius: "8px",
              backgroundColor: getDayColor(day),
              color: day && calData[day] ? "#fff" : day ? "#555" : "transparent",
              fontSize: "13px",
              fontWeight: day && calData[day] ? "bold" : "normal",
              cursor: "default",
              minHeight: "38px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {day || ""}
          </Box>
        ))}
      </Box>

      {/* Legend */}
      <Box sx={{ display: "flex", gap: 2, mt: 2, flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box sx={{ width: "12px", height: "12px", borderRadius: "3px", backgroundColor: "#4caf50" }} />
          <Typography sx={{ fontSize: "12px", color: "#555" }}>Present</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box sx={{ width: "12px", height: "12px", borderRadius: "3px", backgroundColor: "#f44336" }} />
          <Typography sx={{ fontSize: "12px", color: "#555" }}>Absent / Today</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box sx={{ width: "12px", height: "12px", borderRadius: "3px", backgroundColor: "#e0e0e0" }} />
          <Typography sx={{ fontSize: "12px", color: "#555" }}>No Data</Typography>
        </Box>
      </Box>
    </Box>
  );
}
